import merge from 'deepmerge';
import flatten from 'flat';
import jsonata from 'jsonata';
import * as _ from 'lodash';

import { requireInput } from '../errors';

/**
 * List the property values of a given object.
 * @param source Input object.
 *
 * @returns A new array containing the values of the input object's properties.
 */
export function values(source: object): unknown[] {
  requireInput(source, 'source');
  return Object.values(source);
}

/**
 * List the keys of a given object.
 * @param source Input object.
 *
 * @returns A new array indicating the keys of an object.
 */
export function keys(source: object): unknown[] {
  requireInput(source, 'source');
  return Object.keys(source);
}

/**
 * Convert an object into stringified form.
 * @param source Input object.
 *
 * @returns A string which is stringified version of input object.
 */
export function stringify(source: object): string {
  requireInput(source, 'source');
  return JSON.stringify(source);
}

/**
 * Deep merges two given objects.
 * @param source Input object1.
 * @param target Input object2.
 *
 * @returns An Object obtained by deep merging two given objects.
 */
export function deepMerge(source: object, target: object): object {
  requireInput(source, 'source');
  requireInput(target, 'target');
  return merge(source, target);
}

/**
 * Deeply flattens an object.
 * @param source The object to flatten.
 *
 * @returns The flattened object.
 */
export function deepFlatten(source: object): object {
  requireInput(source, 'source');
  return flatten(source);
}

/**
 * Remove a property on an object.
 * @param source The object containing the property to remove.
 * @param property The name of the property to remove.
 *
 * @returns A new object with the specified property removed.
 */
export function remove(source: { [key: string]: unknown }, property: string): object {
  const _source = JSON.parse(JSON.stringify(source)) as { [key: string]: unknown };
  const keysList = property.split('.');

  keysList.reduce((acc, key, index) => {
    if (index === keysList.length - 1) {
      delete acc[key];
      return true;
    }
    return acc[key] || {};
  }, _source);

  return _source;
}

/**
 * Perform a deep comparison between two values to determine if they are equivalent.
 * @param value The value to compare.
 * @param compareValue The other value to compare.
 *
 * @returns True if the values are equivalent. False otherwise.
 *
 */
export function isEqual<T>(value: T, compareValue: T): boolean {
  return _.isEqual(value, compareValue);
}

/**
 * Check if an object contains a property or deeply nested property.
 * @param value The object to search.
 * @param path The JSONata property path of the values to search.
 *
 * @returns True if the property exists. False otherwise.
 *
 */
export async function hasBy<T>(value: T, path: string): Promise<boolean> {
  return (await pluckBy(value, path)) !== undefined;
}

/**
 * Create an object composed of the picked properties.
 * @param value The source object.
 * @param properties The name or names of the properties to pick
 *
 * @returns A new object containing the picked properties.
 *
 */
export function pick<T>(value: T, properties: string | string[]): Partial<T> {
  return _.pick(value, properties);
}

/**
 * Extract the value of a property or deeply nested property of an object.
 * @param value The source object.
 * @param path The JSONata property path of the values to search
 *
 * @returns The value of the plucked property.
 *
 */
export function pluckBy<T>(value: T, path: string): Promise<Partial<T>> {
  return jsonata(path).evaluate(value);
}
